import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { NodeProps } from "@xyflow/react";
import { useUpdateNodeInternals } from "@xyflow/react";
import { useAuth } from "@clerk/react";
import { createAssetsApi } from "@/lib/api/assets";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Image01Icon,
  Video01Icon,
  VoiceIcon,
  Attachment01Icon,
  VideoReplayIcon,
  PlayIcon,
  Cancel01Icon,
  Download01Icon,
  CheckmarkCircle01Icon,
  CancelCircleIcon,
} from "@hugeicons/core-free-icons";
import {
  ImageUploadIcon,
  TextAlignLeftIcon,
  HeadphonesIcon,
  ChartBarLineIcon,
} from "@hugeicons/core-free-icons";
import { Switch } from "@/components/ui/switch";
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store";
import { PARAM_TYPE_TO_DATA_TYPE } from "@/lib/types/model-catalog";
import {
  getTextBuilderInputs,
  getStructuredOutputHandles,
  type OutputSchemaField,
} from "@/lib/registry/text-builder";
import { getCompositorInputs } from "@/lib/registry/compositor";
import type { PipelineNode } from "@/lib/types/pipeline";
import type { ModuleCategory } from "@/lib/types/module";
import { getModuleDefinition } from "@/lib/registry/modules";
import { MODULE_ICONS } from "@/lib/registry/module-icons";
import { NodeHandle } from "./NodeHandle";
import { NodeMenu } from "./NodeMenu";
import { usePipelineStore } from "@/lib/stores/pipeline-store";
import {
  useExecutionStore,
  type NodeStatus,
} from "@/lib/stores/execution-store";
import { useTimingStatsStore } from "@/lib/stores/timing-stats-store";
import { cn } from "@/lib/utils";
import { StreamingMarkdown } from "./StreamingMarkdown";
import { downloadNodeOutput } from "@/lib/utils/download";
import { API_BASE_URL } from "@/lib/api/client";
import { CompositorNodeContent } from "@/components/project/compositor/CompositorNodeContent";

const CATEGORY_HEADER_CLASS: Record<ModuleCategory, string> = {
  input: "bg-blue-500/10 dark:bg-blue-500/15",
  generation: "bg-brand-muted",
  transform: "bg-emerald-500/10 dark:bg-emerald-500/15",
  output: "bg-amber-500/10 dark:bg-amber-500/15",
  utility: "bg-slate-500/10 dark:bg-slate-500/15",
  control: "bg-orange-500/10 dark:bg-orange-500/15",
  agent: "bg-brand-muted",
};

const CATEGORY_ICON_CLASS: Record<ModuleCategory, string> = {
  input: "text-blue-600 dark:text-blue-400",
  generation: "text-brand dark:text-brand-glow",
  transform: "text-emerald-600 dark:text-emerald-400",
  output: "text-amber-600 dark:text-amber-400",
  utility: "text-slate-600 dark:text-slate-400",
  control: "text-orange-600 dark:text-orange-400",
  agent: "text-brand dark:text-brand-glow",
};

function Spinner() {
  return (
    <svg
      className="size-3 animate-spin text-blue-500"
      viewBox="0 0 16 16"
      fill="none"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M14.5 8a6.5 6.5 0 0 0-6.5-6.5"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FloatingStatus({
  status,
  startedAt,
  p50_s,
}: {
  status: NodeStatus | undefined;
  startedAt?: number;
  p50_s?: number;
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (status !== "running" || !startedAt) {
      setElapsed(0);
      return;
    }
    setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [status, startedAt]);

  if (!status || status === "idle") return null;

  const pct =
    status === "running" && p50_s && elapsed > 0
      ? Math.min(99, Math.round((elapsed / p50_s) * 100))
      : null;

  const label =
    status === "pending"
      ? "Queued"
      : status === "running"
        ? pct !== null
          ? `${pct}%`
          : elapsed > 0
            ? `Running... ${elapsed}s`
            : "Running..."
        : status === "completed"
          ? "Done"
          : status === "awaiting_approval"
            ? "Awaiting approval"
            : "Error";

  return (
    <div className="absolute -top-7 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
      <div
        className={cn(
          "flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium shadow-sm border whitespace-nowrap",
          status === "pending" &&
            "bg-yellow-50 dark:bg-yellow-500/10 border-yellow-300/50 dark:border-yellow-500/30 text-yellow-700 dark:text-yellow-400",
          status === "running" &&
            "bg-blue-50 dark:bg-blue-500/10 border-blue-300/50 dark:border-blue-500/30 text-blue-700 dark:text-blue-400",
          status === "completed" &&
            "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300/50 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400",
          status === "failed" &&
            "bg-red-50 dark:bg-red-500/10 border-red-300/50 dark:border-red-500/30 text-red-700 dark:text-red-400",
          status === "awaiting_approval" &&
            "bg-orange-50 dark:bg-orange-500/10 border-orange-300/50 dark:border-orange-500/30 text-orange-700 dark:text-orange-400",
        )}
      >
        {status === "running" ? (
          <Spinner />
        ) : status === "awaiting_approval" ? (
          <span className="size-1.5 rounded-full shrink-0 bg-orange-400 animate-pulse" />
        ) : (
          <span
            className={cn(
              "size-1.5 rounded-full shrink-0",
              status === "pending" && "bg-yellow-500",
              status === "completed" && "bg-emerald-500",
              status === "failed" && "bg-red-500",
            )}
          />
        )}
        {label}
      </div>
    </div>
  );
}

type ImageValue = { dataUrl: string; name: string };

function parseImageValue(value: unknown): ImageValue | null {
  if (!value) return null;
  if (typeof value === "object" && value !== null && "dataUrl" in value)
    return value as ImageValue;
  if (typeof value === "string" && value.startsWith("data:"))
    return { dataUrl: value, name: "Uploaded image" };
  return null;
}

function AssetImagePreview({ assetId }: { assetId: string }) {
  const { getToken } = useAuth();
  const assetsApi = useMemo(() => createAssetsApi({ getToken }), [getToken]);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    let url: string | null = null;
    assetsApi
      .fetchContent(assetId)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch(() => setObjectUrl(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [assetId, assetsApi]);

  if (!objectUrl) {
    return (
      <div className="w-full max-h-36 rounded-lg bg-muted/30 flex items-center justify-center py-6">
        <HugeiconsIcon icon={ImageUploadIcon} size={18} className="text-muted-foreground/30" />
      </div>
    );
  }

  return (
    <img
      src={objectUrl}
      alt="Input"
      className="nopan nodrag w-full object-cover max-h-36 rounded-lg"
    />
  );
}

function FileDropZone({
  accept,
  value,
  onFileChange,
  icon,
  emptyText,
}: {
  accept: string;
  value: string | null;
  onFileChange: (dataUrl: string, fileName: string) => void;
  icon: typeof Image01Icon;
  emptyText: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => onFileChange(e.target?.result as string, file.name);
    reader.readAsDataURL(file);
  };

  const isImagePreview = value?.startsWith("data:image");

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) handleFile(f);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      className={cn(
        "nopan nodrag nowheel flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed cursor-pointer transition-colors overflow-hidden",
        isDragging
          ? "border-blue-400/60 bg-blue-500/5"
          : "border-border/50 bg-muted/30 hover:border-border/80 hover:bg-muted/50",
        !isImagePreview && "py-4 px-3",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
      {isImagePreview ? (
        <img
          src={value!}
          alt="Preview"
          className="w-full object-cover max-h-32 rounded-lg pointer-events-none"
        />
      ) : (
        <>
          <HugeiconsIcon
            icon={icon}
            size={value ? 14 : 18}
            className={
              value ? "text-muted-foreground/60" : "text-muted-foreground/30"
            }
          />
          <span className="text-[10px] text-muted-foreground/50 text-center leading-tight truncate max-w-full px-1">
            {isDragging ? "Drop to upload" : (value ?? emptyText)}
          </span>
        </>
      )}
    </div>
  );
}

function OutputPlaceholder({
  icon,
  label,
}: {
  icon: typeof Image01Icon;
  label?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-7">
      <HugeiconsIcon
        icon={icon}
        size={22}
        className="text-muted-foreground/25"
      />
      <span className="text-[10px] text-muted-foreground/35">
        {label ?? "No output yet"}
      </span>
    </div>
  );
}

function DownloadOverlay({
  nodeOutput,
  nodeLabel,
  children,
}: {
  nodeOutput: Record<string, unknown>;
  nodeLabel: string;
  children: React.ReactNode;
}) {
  const handleDownload = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      downloadNodeOutput(nodeOutput, nodeLabel).catch(console.error);
    },
    [nodeOutput, nodeLabel],
  );

  return (
    <div className="relative group/output">
      {children}
      <button
        type="button"
        className="nopan nodrag absolute top-1.5 right-1.5 size-5 flex items-center justify-center rounded-full bg-black/60 text-white/80 opacity-0 group-hover/output:opacity-100 transition-opacity hover:bg-brand/80"
        onClick={handleDownload}
        title="Download"
      >
        <HugeiconsIcon icon={Download01Icon} size={10} />
      </button>
    </div>
  );
}

function ApprovalGateContent({ nodeStatus }: { nodeStatus: NodeStatus | undefined }) {
  const onApprove = useExecutionStore((s) => s.onApprove);
  const onReject = useExecutionStore((s) => s.onReject);
  const [pendingAction, setPendingAction] = useState<"approve" | "reject" | null>(null);

  const isAwaiting = nodeStatus === "awaiting_approval";
  if (!isAwaiting) return null;

  const handleApprove = async () => {
    if (!onApprove || pendingAction) return;
    setPendingAction("approve");
    try { await onApprove(); } finally { setPendingAction(null); }
  };

  const handleReject = async () => {
    if (!onReject || pendingAction) return;
    setPendingAction("reject");
    try { await onReject(); } finally { setPendingAction(null); }
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[10px] text-muted-foreground text-center">
        Review the results above and choose an action.
      </p>
      <div className="flex gap-1.5">
        <button
          onClick={handleApprove}
          disabled={!!pendingAction}
          className="flex-1 flex items-center justify-center gap-1 rounded-md bg-green-600 px-2 py-1.5 text-[10px] font-medium text-white hover:bg-green-700 disabled:opacity-50 nodrag"
        >
          <HugeiconsIcon icon={CheckmarkCircle01Icon} size={11} />
          {pendingAction === "approve" ? "…" : "Approve"}
        </button>
        <button
          onClick={handleReject}
          disabled={!!pendingAction}
          className="flex items-center justify-center gap-1 rounded-md border border-red-500/40 px-2 py-1.5 text-[10px] font-medium text-red-600 hover:bg-red-500/10 disabled:opacity-50 dark:text-red-400 nodrag"
        >
          <HugeiconsIcon icon={CancelCircleIcon} size={11} />
          {pendingAction === "reject" ? "…" : "Reject"}
        </button>
      </div>
    </div>
  );
}

const ERROR_TYPE_TITLES: Record<string, string> = {
  invalid_config: "Invalid settings",
  invalid_input: "Invalid input",
  rate_limited: "Rate limited",
  provider_overloaded: "Temporarily unavailable",
  timeout: "Timed out",
  network: "Connection error",
  content_filtered: "Content blocked",
  quota_exceeded: "Insufficient credits",
  provider_error: "Provider error",
  internal: "Something went wrong",
};

function NodeContent({
  nodeId,
  module,
  config,
  nodeOutput,
  nodeStatus,
  nodeStreamingText,
  nodeError,
  nodeErrorType,
  nodeErrorRetryable: _nodeErrorRetryable,
  nodeErrorSuggestion,
  nodeLabel,
  onConfigChange,
}: {
  nodeId: string;
  module: string;
  config: Record<string, unknown>;
  nodeOutput: Record<string, unknown> | undefined;
  nodeStatus: NodeStatus | undefined;
  nodeStreamingText: string | undefined;
  nodeError: string | undefined;
  nodeErrorType: string | undefined;
  nodeErrorRetryable: boolean | undefined;
  nodeErrorSuggestion: string | undefined;
  nodeLabel: string;
  onConfigChange: (nodeId: string, update: Record<string, unknown>) => void;
}) {
  const completed = nodeStatus === "completed";

  // Show error message when node has failed
  if (nodeStatus === "failed" && nodeError) {
    const title =
      (nodeErrorType && ERROR_TYPE_TITLES[nodeErrorType]) ?? "Something went wrong";

    // For validation errors the message contains "; "-separated items already
    const items = nodeError.split("; ").filter(Boolean);

    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-2.5 py-2 flex flex-col gap-1.5">
        <span className="text-[10px] font-medium text-destructive">{title}</span>
        {items.map((item, i) => (
          <span
            key={i}
            className="text-[11px] leading-relaxed text-destructive/80 break-words"
          >
            {items.length > 1 && "· "}
            {item}
          </span>
        ))}
        {nodeErrorSuggestion && (
          <span className="text-[10px] text-muted-foreground/60 italic mt-0.5">
            {nodeErrorSuggestion}
          </span>
        )}
      </div>
    );
  }

  switch (module) {
    case "text_input": {
      return (
        <textarea
          value={String(config.text ?? "")}
          onChange={(e) => onConfigChange(nodeId, { text: e.target.value })}
          placeholder="Enter text..."
          rows={3}
          className="nopan nodrag nowheel w-full rounded-lg bg-black/5 dark:bg-white/10 px-2.5 py-2 text-[11px] leading-relaxed text-foreground placeholder:text-muted-foreground/40 outline-none resize-none border-none"
        />
      );
    }

    case "llm_text": {
      const isStreaming = nodeStatus === "running";
      const schema = Array.isArray(config.output_schema)
        ? (config.output_schema as OutputSchemaField[])
        : [];
      const isStructured = schema.length > 0;

      // ── Structured mode ────────────────────────────────────────────────────
      if (isStructured) {
        return (
          <div className="flex flex-col gap-2">
            {/* Completed structured output: field → value list */}
            {completed && nodeOutput && Object.keys(nodeOutput).length > 0 && (
              <div className="grid grid-cols-1 gap-1.5 mt-1">
                {schema.map((field) => {
                  const val = nodeOutput[field.name];
                  const display = val == null ? "—" : String(val);
                  return (
                    <div
                      key={field.name}
                      className="flex flex-col justify-center rounded-lg bg-black/[0.03] dark:bg-white/[0.04] border border-border/40 px-3 py-2"
                    >
                      <span className="text-[9px] font-semibold text-muted-foreground/50 uppercase tracking-wider mb-0.5">
                        {field.name}
                      </span>
                      <span
                        className={cn(
                          "text-[11px] leading-relaxed break-words",
                          val == null
                            ? "text-muted-foreground/30 italic"
                            : "text-foreground",
                        )}
                      >
                        {display}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* During streaming or fallback raw JSON text */}
            {(!completed ||
              !nodeOutput ||
              Object.keys(nodeOutput).length === 0) && (
              <div className="flex flex-col gap-1 mt-1">
                {nodeStreamingText && nodeStreamingText !== "" ? (
                  <StreamingMarkdown
                    text={nodeStreamingText}
                    isStreaming={isStreaming}
                  />
                ) : (
                  <OutputPlaceholder
                    icon={TextAlignLeftIcon}
                    label={
                      isStreaming
                        ? "Generating JSON..."
                        : "Waiting for output..."
                    }
                  />
                )}
              </div>
            )}

            {/* Show raw text after completion below if we have it and want to inspect */}
            {completed &&
              nodeOutput &&
              Object.keys(nodeOutput).length > 0 &&
              !!nodeOutput?.text && (
                <div className="mt-2 pt-2 border-t border-border/20">
                  <span className="text-[9px] font-medium text-muted-foreground/40 uppercase mb-1 block">
                    Raw Output
                  </span>
                  <div className="max-h-24 overflow-y-auto">
                    <StreamingMarkdown
                      text={String(nodeOutput.text)}
                      isStreaming={false}
                    />
                  </div>
                </div>
              )}
          </div>
        );
      }

      // ── Plain text mode ────────────────────────────────────────────────────
      const displayText =
        nodeStreamingText && nodeStreamingText !== ""
          ? nodeStreamingText
          : (nodeOutput?.text as string | undefined);

      return (
        <div className="flex flex-col gap-2">
          {displayText != null && displayText !== "" ? (
            (() => {
              const content = (
                <StreamingMarkdown
                  text={displayText}
                  isStreaming={isStreaming}
                />
              );
              return completed && nodeOutput?.text ? (
                <DownloadOverlay nodeOutput={nodeOutput} nodeLabel={nodeLabel}>
                  {content}
                </DownloadOverlay>
              ) : (
                content
              );
            })()
          ) : (
            <OutputPlaceholder
              icon={TextAlignLeftIcon}
              label={isStreaming ? "Generating..." : "Output will appear here"}
            />
          )}
        </div>
      );
    }

    case "image_gen":
    case "background_remover":
    case "image_upscaler": {
      if (completed && nodeOutput?.image) {
        return (
          <DownloadOverlay nodeOutput={nodeOutput} nodeLabel={nodeLabel}>
            <img
              src={`${API_BASE_URL}/api/assets/${String(nodeOutput.image)}`}
              alt="Output"
              className="w-full rounded-lg border border-border/20 object-cover max-h-40"
            />
          </DownloadOverlay>
        );
      }
      return <OutputPlaceholder icon={Image01Icon} />;
    }

    case "video_gen": {
      if (completed && nodeOutput?.video) {
        return (
          <DownloadOverlay nodeOutput={nodeOutput} nodeLabel={nodeLabel}>
            <video
              src={`${API_BASE_URL}/api/assets/${String(nodeOutput.video)}`}
              controls
              className="nopan nodrag nowheel w-full rounded-lg border border-border/20 max-h-40 bg-black"
            />
          </DownloadOverlay>
        );
      }
      return <OutputPlaceholder icon={Video01Icon} />;
    }

    case "audio_tts":
    case "audio_music": {
      if (completed && nodeOutput?.audio) {
        return (
          <DownloadOverlay nodeOutput={nodeOutput} nodeLabel={nodeLabel}>
            <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/30 px-3 py-3">
              <HugeiconsIcon
                icon={VoiceIcon}
                size={14}
                className="text-muted-foreground"
              />
              <span className="text-[10px] text-muted-foreground">
                Audio ready
              </span>
            </div>
          </DownloadOverlay>
        );
      }
      return <OutputPlaceholder icon={VoiceIcon} />;
    }

    case "file_input": {
      const fileConfig = config.file as Record<string, unknown> | string | undefined;
      const label =
        fileConfig && typeof fileConfig === "object" && "name" in fileConfig
          ? String(fileConfig.name)
          : fileConfig
            ? String(fileConfig)
            : null;

      return (
        <FileDropZone
          accept="*/*"
          value={label}
          onFileChange={(_, fileName) => onConfigChange(nodeId, { file: fileName })}
          icon={Attachment01Icon}
          emptyText="Drop file or click to browse"
        />
      );
    }

    case "video_assembly": {
      if (completed && nodeOutput?.video) {
        return (
          <DownloadOverlay nodeOutput={nodeOutput} nodeLabel={nodeLabel}>
            <video
              src={`${API_BASE_URL}/api/assets/${String(nodeOutput.video)}`}
              controls
              className="nopan nodrag nowheel w-full rounded-lg border border-border/20 max-h-40 bg-black"
            />
          </DownloadOverlay>
        );
      }
      return <OutputPlaceholder icon={VideoReplayIcon} />;
    }

    case "number_input": {
      return (
        <input
          type="number"
          value={String(config.value ?? 0)}
          onChange={(e) =>
            onConfigChange(nodeId, { value: Number(e.target.value) })
          }
          className="nopan nodrag w-full rounded-lg bg-black/5 dark:bg-white/10 px-2.5 py-2 text-[11px] text-foreground outline-none border-none text-center"
        />
      );
    }

    case "boolean_input": {
      const val = Boolean(config.value);
      return (
        <div className="flex items-center justify-between px-1 py-1">
          <span className="text-[11px] text-muted-foreground">
            {val ? "True" : "False"}
          </span>
          <Switch
            checked={val}
            onCheckedChange={(checked) =>
              onConfigChange(nodeId, { value: checked })
            }
            className="nopan nodrag scale-75 origin-right"
          />
        </div>
      );
    }

    case "image_input": {
      const imgConfig = config.image as Record<string, unknown> | undefined;

      // New: AssetRef with assetId
      if (imgConfig && "assetId" in imgConfig && typeof imgConfig.assetId === "string") {
        return (
          <div className="relative group/img rounded-lg overflow-hidden">
            <AssetImagePreview assetId={imgConfig.assetId} />
            <button
              className="nopan nodrag absolute top-1.5 right-1.5 size-5 flex items-center justify-center rounded-full bg-black/60 text-white/80 opacity-0 group-hover/img:opacity-100 transition-opacity hover:bg-red-500/70"
              onClick={(e) => {
                e.stopPropagation();
                onConfigChange(nodeId, { image: null });
              }}
              title="Remove image"
            >
              <HugeiconsIcon icon={Cancel01Icon} size={9} />
            </button>
          </div>
        );
      }

      // Legacy: base64 dataUrl
      const imageData = parseImageValue(config.image);
      if (imageData) {
        return (
          <div className="relative group/img rounded-lg overflow-hidden">
            <img
              src={imageData.dataUrl}
              alt="Input"
              className="nopan nodrag w-full object-cover max-h-36"
            />
            <button
              className="nopan nodrag absolute top-1.5 right-1.5 size-5 flex items-center justify-center rounded-full bg-black/60 text-white/80 opacity-0 group-hover/img:opacity-100 transition-opacity hover:bg-red-500/70"
              onClick={(e) => {
                e.stopPropagation();
                onConfigChange(nodeId, { image: null });
              }}
              title="Remove image"
            >
              <HugeiconsIcon icon={Cancel01Icon} size={9} />
            </button>
          </div>
        );
      }

      // No image: show drop zone
      return (
        <FileDropZone
          accept="image/*"
          value={null}
          onFileChange={(dataUrl, fileName) =>
            onConfigChange(nodeId, { image: { dataUrl, name: fileName } })
          }
          icon={ImageUploadIcon}
          emptyText="Drop image or click to browse"
        />
      );
    }

    case "text_builder": {
      return (
        <textarea
          value={String(config.template ?? "")}
          onChange={(e) => onConfigChange(nodeId, { template: e.target.value })}
          placeholder="Write a {{tone}} email about {{topic}}..."
          rows={3}
          className="nopan nodrag nowheel w-full rounded-lg bg-black/5 dark:bg-white/10 px-2.5 py-2 text-[11px] leading-relaxed text-foreground placeholder:text-muted-foreground/40 outline-none resize-none border-none"
        />
      );
    }

    case "text_output": {
      if (completed && nodeOutput?.text != null) {
        return (
          <DownloadOverlay nodeOutput={nodeOutput} nodeLabel={nodeLabel}>
            <div className="text-[10px] leading-relaxed text-muted-foreground line-clamp-4 whitespace-pre-wrap break-words">
              {String(nodeOutput.text)}
            </div>
          </DownloadOverlay>
        );
      }
      return (
        <OutputPlaceholder
          icon={TextAlignLeftIcon}
          label="Text will appear here"
        />
      );
    }

    case "image_output": {
      if (completed && nodeOutput?.image) {
        return (
          <DownloadOverlay nodeOutput={nodeOutput} nodeLabel={nodeLabel}>
            <img
              src={`${API_BASE_URL}/api/assets/${String(nodeOutput.image)}`}
              alt="Output"
              className="w-full rounded-lg border border-border/20 object-cover max-h-40"
            />
          </DownloadOverlay>
        );
      }
      return <OutputPlaceholder icon={Image01Icon} />;
    }

    case "audio_output": {
      if (completed && nodeOutput?.audio) {
        return (
          <div className="flex flex-col gap-1">
            <audio
              controls
              src={`${API_BASE_URL}/api/assets/${String(nodeOutput.audio)}`}
              className="nopan nodrag nowheel w-full h-8"
            />
            <button
              type="button"
              className="nopan nodrag self-end flex items-center gap-1 text-[10px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                downloadNodeOutput(nodeOutput, nodeLabel).catch(console.error);
              }}
            >
              <HugeiconsIcon icon={Download01Icon} size={10} />
              Download
            </button>
          </div>
        );
      }
      return (
        <OutputPlaceholder
          icon={HeadphonesIcon}
          label="Audio will appear here"
        />
      );
    }

    case "number_output": {
      if (completed && nodeOutput?.value != null) {
        return (
          <div className="flex items-center justify-center py-3">
            <span className="text-2xl font-semibold tabular-nums text-foreground">
              {Number(nodeOutput.value).toLocaleString()}
            </span>
          </div>
        );
      }
      return (
        <OutputPlaceholder
          icon={ChartBarLineIcon}
          label="Number will appear here"
        />
      );
    }

    case "image_converter":
    case "image_cropper":
    case "image_resizer": {
      if (completed && nodeOutput?.image) {
        return (
          <DownloadOverlay nodeOutput={nodeOutput} nodeLabel={nodeLabel}>
            <img
              src={`${API_BASE_URL}/api/assets/${String(nodeOutput.image)}`}
              alt="Output"
              className="w-full rounded-lg border border-border/20 object-cover max-h-40"
            />
          </DownloadOverlay>
        );
      }
      return <OutputPlaceholder icon={Image01Icon} />;
    }

    case "text_formatter": {
      if (completed && nodeOutput?.text != null) {
        return (
          <DownloadOverlay nodeOutput={nodeOutput} nodeLabel={nodeLabel}>
            <div className="text-[10px] leading-relaxed text-muted-foreground line-clamp-4 whitespace-pre-wrap break-words">
              {String(nodeOutput.text)}
            </div>
          </DownloadOverlay>
        );
      }
      return (
        <OutputPlaceholder icon={TextAlignLeftIcon} label="Formatted text" />
      );
    }

    case "approval_gate": {
      return <ApprovalGateContent nodeStatus={nodeStatus} />;
    }

    case "compositor": {
      return (
        <CompositorNodeContent
          nodeId={nodeId}
          config={config}
          onConfigChange={onConfigChange}
        />
      )
    }

    default:
      return null;
  }
}

function ModuleNodeInner({ id, data, selected }: NodeProps<PipelineNode>) {
  const moduleDef = getModuleDefinition(data.module);
  const updateNodeLabel = usePipelineStore((s) => s.updateNodeLabel);
  const updateNodeConfig = usePipelineStore((s) => s.updateNodeConfig);
  const toggleNodeCollapsed = usePipelineStore((s) => s.toggleNodeCollapsed);
  const collapsed = usePipelineStore((s) => s.collapsedNodeIds.has(id));
  const nodeStatus: NodeStatus | undefined = useExecutionStore(
    (s) => s.nodeStatuses[id],
  );
  const nodeStartedAt: number | undefined = useExecutionStore(
    (s) => s.nodeStartedAt[id],
  );
  const nodeOutput: Record<string, unknown> | undefined = useExecutionStore(
    (s) => s.nodeOutputs[id],
  );
  const nodeStreamingText: string | undefined = useExecutionStore(
    (s) => s.nodeStreamingText[id],
  );
  const nodeError: string | undefined = useExecutionStore(
    (s) => s.nodeErrors[id],
  );
  const nodeErrorType: string | undefined = useExecutionStore(
    (s) => s.nodeErrorTypes[id],
  );
  const nodeErrorRetryable: boolean | undefined = useExecutionStore(
    (s) => s.nodeErrorRetryable[id],
  );
  const nodeErrorSuggestion: string | undefined = useExecutionStore(
    (s) => s.nodeErrorSuggestions[id],
  );
  const onRunNode = useExecutionStore((s) => s.onRunNode);
  const isRunning = useExecutionStore((s) => s.isRunning);
  const getModelById = useModelCatalogStore((s) => s.getById);
  const timingStats = useTimingStatsStore((s) => s.stats);
  const modelApiId: string | undefined =
    typeof data.config?.model === "string" ? data.config.model : undefined;
  const p50_s = modelApiId ? timingStats[modelApiId]?.p50_s : undefined;
  const focusedNodeId = usePipelineStore((s) => s.focusedNodeId);
  const focusNode = usePipelineStore((s) => s.focusNode);
  const [isPulsing, setIsPulsing] = useState(false);
  const updateNodeInternals = useUpdateNodeInternals();
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(data.label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isRenaming]);

  const handleRenameStart = useCallback(() => {
    setRenameValue(data.label);
    setIsRenaming(true);
  }, [data.label]);

  const handleRenameCommit = useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== data.label) {
      updateNodeLabel(id, trimmed);
    }
    setIsRenaming(false);
  }, [id, renameValue, data.label, updateNodeLabel]);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleRenameCommit();
      if (e.key === "Escape") setIsRenaming(false);
    },
    [handleRenameCommit],
  );

  const handleToggleCollapse = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleNodeCollapsed(id);
    },
    [id, toggleNodeCollapsed],
  );

  const handleRunNode = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRunNode?.(id);
    },
    [id, onRunNode],
  );

  const outputSchema =
    data.module === "llm_text" &&
    Array.isArray(data.config.output_schema) &&
    (data.config.output_schema as OutputSchemaField[]).length > 0
      ? (data.config.output_schema as OutputSchemaField[])
      : null;

  const handleOutputs = moduleDef
    ? outputSchema
      ? getStructuredOutputHandles(outputSchema)
      : moduleDef.outputs.filter((o) => o.handle)
    : [];

  const handleOutputSig = handleOutputs.map((h) => h.name).join("\0");
  useEffect(() => {
    updateNodeInternals(id);
  }, [handleOutputSig, id, updateNodeInternals]);

  useEffect(() => {
    if (focusedNodeId !== id) return;
    setIsPulsing(true);
    const t = setTimeout(() => {
      setIsPulsing(false);
      focusNode(null);
    }, 1200);
    return () => clearTimeout(t);
  }, [focusedNodeId, id, focusNode]);

  if (!moduleDef) return null;

  const modelKey = data.config.model ? String(data.config.model) : null;
  const modelEntry = modelKey ? getModelById(modelKey) : undefined;
  const modelSubtitle = modelEntry?.display_name ?? modelKey ?? null;

  // Compute input handles — AI nodes use catalog params, others use module definition
  type HandleItem = {
    name: string;
    label?: string;
    type: import("@/lib/types/module").DataType;
    required?: boolean;
  };
  let handleInputs: HandleItem[];
  let showSelectModel = false;

  if (data.module === "text_builder") {
    handleInputs = getTextBuilderInputs(String(data.config.template ?? ""));
  } else if (data.module === "compositor") {
    handleInputs = getCompositorInputs(data.config).map((i) => ({
      ...i,
      label: i.description,
    }));
  } else if (moduleDef.modelConfig) {
    if (!modelEntry) {
      handleInputs = [];
      showSelectModel = true;
    } else {
      const modelParams = modelEntry.parameters_schema.params.map((p) => ({
        name: p.name,
        label: p.label,
        type: PARAM_TYPE_TO_DATA_TYPE[p.type],
        required: p.required,
      }));
      // Also include registry handle inputs not covered by model params (e.g. "image" on video_gen)
      const modelParamNames = new Set(
        modelEntry.parameters_schema.params.map((p) => p.name),
      );
      const registryOnlyHandles = moduleDef.inputs
        .filter((i) => i.handle && !modelParamNames.has(i.name))
        .map((i) => ({ name: i.name, type: i.type, required: i.required }));
      handleInputs = [...modelParams, ...registryOnlyHandles];
    }
  } else {
    handleInputs = moduleDef.inputs.filter((i) => i.handle);
  }

  // Notify React Flow when dynamic input handles change (e.g. compositor layers)
  const handleInputSig = handleInputs.map((h) => h.name).join("\0");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { updateNodeInternals(id); }, [handleInputSig]);

  const icon = MODULE_ICONS[moduleDef.icon];

  const contentElement = !collapsed ? (
    <NodeContent
      nodeId={id}
      module={data.module}
      config={data.config}
      nodeOutput={nodeOutput}
      nodeStatus={nodeStatus}
      nodeStreamingText={nodeStreamingText}
      nodeError={nodeError}
      nodeErrorType={nodeErrorType}
      nodeErrorRetryable={nodeErrorRetryable}
      nodeErrorSuggestion={nodeErrorSuggestion}
      nodeLabel={data.label}
      onConfigChange={updateNodeConfig}
    />
  ) : null;

  const hasContent = contentElement !== null;

  return (
    <div className="relative">
      {/* Floating status badge above node */}
      <FloatingStatus status={nodeStatus} startedAt={nodeStartedAt} p50_s={p50_s} />

      {/* Node body */}
      <div
        className={cn(
          "group/node min-w-[220px] max-w-[300px] rounded-xl border bg-card text-card-foreground shadow-sm transition-shadow",
          selected
            ? "ring-2 ring-brand shadow-lg border-brand/30"
            : "border-border/50 dark:border-border",
          !selected &&
            nodeStatus === "running" &&
            "ring-2 ring-blue-400/50 animate-pulse",
          !selected &&
            nodeStatus === "completed" &&
            "ring-2 ring-emerald-400/30",
          !selected && nodeStatus === "failed" && "ring-2 ring-red-400/50",
          !selected &&
            nodeStatus === "awaiting_approval" &&
            "ring-2 ring-orange-400/60 animate-pulse",
          isPulsing && "animate-ring-pulse",
        )}
      >
        {/* Header */}
        <div
          className={cn(
            "flex items-center gap-2 px-3 py-2 border-b border-border/30",
            collapsed ? "rounded-xl border-b-0" : "rounded-t-xl",
            CATEGORY_HEADER_CLASS[moduleDef.category],
          )}
        >
          <button
            onClick={handleToggleCollapse}
            className="relative shrink-0 size-4 flex items-center justify-center"
          >
            {icon && (
              <HugeiconsIcon
                icon={icon}
                size={16}
                className={cn(
                  CATEGORY_ICON_CLASS[moduleDef.category],
                  "absolute inset-0 m-auto transition-opacity duration-150 group-hover/node:opacity-0",
                )}
              />
            )}
            <HugeiconsIcon
              icon={collapsed ? ArrowDown01Icon : ArrowUp01Icon}
              size={14}
              className="absolute inset-0 m-auto text-muted-foreground opacity-0 transition-opacity duration-150 group-hover/node:opacity-100"
            />
          </button>
          {isRenaming ? (
            <input
              ref={inputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={handleRenameCommit}
              onKeyDown={handleRenameKeyDown}
              className="text-xs font-medium flex-1 bg-transparent border-b border-foreground/30 outline-none py-0"
            />
          ) : (
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate leading-tight">
                {data.label}
              </div>
              {modelSubtitle && (
                <div className="text-[10px] text-muted-foreground/60 truncate leading-tight mt-0.5">
                  {modelSubtitle}
                </div>
              )}
            </div>
          )}
          <button
            onClick={handleRunNode}
            disabled={
              isRunning || nodeStatus === "running" || nodeStatus === "pending"
            }
            className="flex size-5 items-center justify-center rounded-md opacity-0 group-hover/node:opacity-100 hover:bg-black/10 dark:hover:bg-white/10 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
            title="Run this node"
          >
            <HugeiconsIcon
              icon={PlayIcon}
              size={11}
              className="text-foreground/70"
            />
          </button>
          <NodeMenu
            nodeId={id}
            nodeLabel={data.label}
            nodeOutput={nodeOutput}
            onRename={handleRenameStart}
          />
        </div>

        {/* Content body — only when expanded */}
        {hasContent && (
          <div className="px-2 py-2">
            {contentElement}
          </div>
        )}
      </div>

      {/* Input handles — absolutely positioned along left edge */}
      {showSelectModel ? (
        <div
          className="absolute left-0 top-0 bottom-0 flex items-center pointer-events-none"
          style={{ width: 0 }}
        >
          <span
            className="absolute left-3 text-[9px] text-muted-foreground/40 whitespace-nowrap"
            style={{ transform: "translateX(-100%)" }}
          >
            Select a model
          </span>
        </div>
      ) : handleInputs.length > 0 ? (
        <div
          className={cn(
            "absolute left-0 top-0 flex flex-col pointer-events-none",
            collapsed ? "bottom-0 justify-center" : "gap-6 pt-[38px]",
          )}
          style={{ width: 0 }}
        >
          {handleInputs.map((input) => (
            <NodeHandle
              key={input.name}
              name={input.name}
              label={"label" in input ? input.label : undefined}
              type={input.type}
              direction="input"
              required={input.required}
              showLabel={selected && !collapsed}
              disableHover={collapsed}
            />
          ))}
        </div>
      ) : null}

      {/* Output handles — absolutely positioned along right edge */}
      {handleOutputs.length > 0 && (
        <div
          className={cn(
            "absolute right-0 top-0 flex flex-col pointer-events-none",
            collapsed ? "bottom-0 justify-center" : "gap-6 pt-[38px]",
          )}
          style={{ width: 0 }}
        >
          {handleOutputs.map((output) => (
            <NodeHandle
              key={output.name}
              name={output.name}
              type={output.type}
              direction="output"
              showLabel={selected && !collapsed}
              disableHover={collapsed}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export const ModuleNode = memo(ModuleNodeInner);
